import React, { useState, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import BookSlotModal from './booking/BookSlotModal';
import { bookingConfigurationQuery, list } from '@/graph/bookings';
import groupBy from '@/util/groupBy';
import rooms from '../../../graph/rooms';
import { ROOM_TYPES } from '../../entities/room/enums';
import {
  format,
  addMonths,
  subMonths,
  getDay,
  subDays,
  addDays,
  getMonth,
  getYear,
  getDaysInMonth,
  isWeekend,
} from 'date-fns';
import chunk from 'lodash/chunk';
import Week from './Week';
import { Subscription } from '@apollo/client/react/components';
import { withQuery } from '@apollo/client/react/hoc';
import { Loading } from '@/components/pages/blockSchedule/Loading';
import { useQuery } from '@apollo/client';

const Schedule = styled.div`
  font-size: 0.75em;
`;

export const padWeeks = days => {
  const differenceBefore = Math.max(getDay(days[0]) - 1);
  const differenceAfter = Math.max(5 - getDay(days[days.length - 1]));
  const padLeft = [...new Array(differenceBefore)].map((_, i) => subDays(days[0], differenceBefore - i));
  const padRight = [...new Array(differenceAfter)].map((_, i) => addDays(days[days.length - 1], i + 1));
  return [...padLeft, ...days, ...padRight];
};

export const thisMonthWorkdays = date => {
  const month = getMonth(date);
  const year = getYear(date);
  return [...new Array(getDaysInMonth(date))].map((_, i) => new Date(year, month, i + 1)).filter(_ => !isWeekend(_));
};

const BlockScheduleCalendar = ({
  data,
  loading,
  modal,
  setModal,
  weeks,
  rooms,
  date,
  physician,
  colorMode,
  dayStartTime,
  dayEndTime,
}) => {
  const bookings = useMemo(
    () => groupBy(data?.bookings || [], ({ roomId, date }) => `${roomId}_${date}`),
    [data?.bookings]
  );

  const handleCellClick = useCallback(val => setModal(val), [setModal]);

  if (loading) {
    return <Loading label={'Loading schedule data...'} />;
  }

  return (
    <Schedule>
      {modal && (
        <BookSlotModal {...(modal || {})} onClose={setModal} dayStartTime={dayStartTime} dayEndTime={dayEndTime} />
      )}
      {weeks.map((dates, i) => (
        <Week
          key={i}
          colorMode={colorMode}
          rooms={rooms}
          dates={dates}
          bookings={bookings}
          dateSelection={date}
          onCellClick={handleCellClick}
          showDayNames={i === 0}
          physicianSelection={physician}
          firstWeek={i === 0}
          dayStartTime={dayStartTime}
          dayEndTime={dayEndTime}
        />
      ))}
    </Schedule>
  );
};

const BlockSchedule = ({ data, date, physician, colorMode }) => {
  const [modalData, setModalData] = useState(null);

  const { data: configData, loading: configLoading } = useQuery(bookingConfigurationQuery);

  const rooms = useMemo(
    () => (data.rooms || []).filter(_ => _.type === ROOM_TYPES.OR).sort((a, b) => a.name.localeCompare(b.name)),
    [data.rooms]
  );

  const weeks = useMemo(() => chunk(padWeeks(thisMonthWorkdays(date)), 5), [date]);

  const variables = useMemo(
    () => ({
      from: format(weeks?.at(0)?.at(0), 'YYYY-MM-DD'),
      to: format(weeks?.at(-1)?.at(-1), 'YYYY-MM-DD'),
    }),
    [weeks]
  );

  if (configLoading) {
    return <Loading label={'Loading configuration...'} />;
  }

  const dayStartTime = configData?.bookingConfiguration?.dayStartTime || '05:00';
  const dayEndTime = configData?.bookingConfiguration?.dayEndTime || '17:00';

  return (
    <Subscription subscription={list} variables={variables}>
      {({ data: bookingData, loading: bookingLoading }) => (
        <BlockScheduleCalendar
          data={bookingData}
          loading={bookingLoading}
          colorMode={colorMode}
          weeks={weeks}
          rooms={rooms}
          modal={modalData}
          setModal={setModalData}
          date={date}
          physician={physician}
          dayStartTime={dayStartTime}
          dayEndTime={dayEndTime}
        />
      )}
    </Subscription>
  );
};

export default withQuery(rooms.list)(BlockSchedule);
